import {
  PaintArea,
  PaintStatus,
} from '@/components/VehiclePaintMeasurement/VehiclePaintMeasurement.types';
import { PaintPoint, PaintPointNumber } from '@/components/VehiclePaintPoints/VehiclePaintPoints.types';
import { GapPoint, GapStatus, GapPosition } from '@/components/VehicleGapDetection/VehicleGapDetection.types';

/**
 * API Types from generated.ts
 */
interface PaintThicknessMeasurementDto {
  nameId?: string;
  isFixed?: boolean;
  notApplicable?: boolean;
  minMeasurement?: number;
  maxMeasurement?: number;
}

interface GapMeasurementDto {
  nameId?: string;
  isFixed?: boolean;
  notApplicable?: boolean;
  status?: 'OK' | 'NOT_OK';
}

interface PaintThicknessAndGapMeasurementsSubsectionDto {
  paintThicknessMeasurements?: {
    [key: string]: PaintThicknessMeasurementDto;
  };
  gapsMeasurements?: {
    [key: string]: GapMeasurementDto;
  };
  expertSummary?: string;
}

/**
 * Mapowanie nameId na widok pojazdu dla Gap Points (A-J)
 * UWAGA: To mapowanie jest używane tylko dla pomiarów szczelin (gapsMeasurements).
 * Dla paintAreas i paintPoints pozycje są definiowane w konfiguracji komponentów.
 */
const gapNameIdToViewMap: Record<string, 'left' | 'top' | 'right'> = {
  A: 'left',   // Maska
  B: 'left',   // Przedni błotnik lewy
  C: 'left',   // Przednie drzwi lewe
  D: 'left',   // Tylne drzwi lewe
  E: 'left',   // Tylny błotnik lewy
  F: 'left',   // Pokrywa bagażnika
  G: 'top',    // Dach
  H: 'right',  // Przedni błotnik prawy
  I: 'right',  // Przednie drzwi prawe
  J: 'right',  // Tylne drzwi prawe
};

/**
 * Konwersja wartości pomiaru na status PaintStatus
 */
const measurementValueToPaintStatus = (value?: number | null): PaintStatus => {
  if (value == null) return 'MISSING';

  if (value < 50) return 'RANGE_0_50';
  if (value < 100) return 'RANGE_50_100';
  if (value < 150) return 'RANGE_100_150';
  if (value < 200) return 'RANGE_150_200';
  if (value < 250) return 'RANGE_200_250';
  if (value < 300) return 'RANGE_250_300';
  if (value < 400) return 'RANGE_300_400';
  if (value < 900) return 'RANGE_400_900';
  return 'RANGE_900_PLUS';
};

/**
 * Sprawdza czy nameId jest numerem punktu pomiarowego (12-32)
 */
const isPaintPoint = (nameId: string): boolean => {
  const num = parseInt(nameId, 10);
  return !isNaN(num) && num >= 12 && num <= 32;
};

/**
 * Sprawdza czy nameId jest numerem obszaru (1-11)
 */
const isPaintArea = (nameId: string): boolean => {
  const num = parseInt(nameId, 10);
  return !isNaN(num) && num >= 1 && num <= 11;
};

/**
 * Mapowanie danych pomiarów lakieru obszarów (VehiclePaintMeasurement)
 * Mapuje nameId 1-11 (obszary lakieru) z API do struktury UI
 */
export const mapPaintThicknessMeasurementsToAreas = (
  measurements?: { [key: string]: PaintThicknessMeasurementDto },
): PaintArea[] => {
  if (!measurements) return [];

  return Object.entries(measurements)
    .filter(([nameId]) => isPaintArea(nameId))
    .map(([nameId, measurement]) => {
      // Używamy minMeasurement dla określenia statusu (zakres kolorów)
      // bo maxMeasurement to górna granica zakresu i przy strict < wpada w następny bucket
      const displayValue = measurement.minMeasurement ?? measurement.maxMeasurement ?? undefined;

      const isMeasured = !measurement.notApplicable;
      const notApplicable = measurement.notApplicable || false;
      const status = notApplicable
        ? 'NOT_APPLICABLE'
        : measurementValueToPaintStatus(displayValue);

      return {
        position: nameId,
        status,
        value: displayValue,
        isFixed: measurement.isFixed,
        isMeasured,
      };
    });
};

/**
 * Mapowanie danych pomiarów lakieru punktów (VehiclePaintPoints)
 * Mapuje nameId 12-32 (punkty pomiarowe) z API do struktury UI
 *
 * UWAGA: Pozycje punktów (top, left) są definiowane w konfiguracji komponentu
 * paintPointsMapping.ts, nie w mapperze. Mapper zwraca tylko dane z API.
 *
 * Komponenty VehiclePaintPoints używają paintPointsMapping.ts aby określić
 * pozycje punktów dla danego typu pojazdu i widoku.
 */
export const mapPaintThicknessMeasurementsToPoints = (
  measurements?: { [key: string]: PaintThicknessMeasurementDto },
): PaintPoint[] => {
  if (!measurements) return [];

  return Object.entries(measurements)
    .filter(([nameId]) => isPaintPoint(nameId))
    .map(([nameId, measurement]) => {
      // Używamy minMeasurement dla określenia statusu
      const displayValue = measurement.minMeasurement ?? measurement.maxMeasurement ?? undefined;

      const isMeasured = !measurement.notApplicable;

      return {
        pointNumber: nameId as PaintPointNumber, // Cast to PaintPointNumber (12-32)
        value: isMeasured ? displayValue : undefined,
        isFixed: measurement.isFixed,
        isMeasured,
      };
    });
};

/**
 * Mapowanie statusu szczeliny z API na GapStatus
 */
const mapGapStatus = (status?: 'OK' | 'NOT_OK', notApplicable?: boolean): GapStatus => {
  if (notApplicable) return 'BRAK';
  if (status === 'OK') return 'OK';
  if (status === 'NOT_OK') return 'NIE_OK';
  return 'BRAK';
};

/**
 * Mapowanie danych pomiarów szczelin (VehicleGapDetection)
 */
export const mapGapMeasurementsToPoints = (
  measurements?: { [key: string]: GapMeasurementDto },
): GapPoint[] => {
  if (!measurements) return [];

  return Object.entries(measurements)
    .filter(([nameId]) => /^[A-J]$/.test(nameId)) // Punkty szczelin A-J
    .map(([nameId, measurement]) => {
      const view = gapNameIdToViewMap[nameId] || 'left';

      return {
        position: nameId as GapPosition, // Cast to GapPosition (A-J)
        status: mapGapStatus(measurement.status, measurement.notApplicable),
        view,
      };
    });
};

/**
 * Zunifikowany typ pomiaru dla wszystkich nameId (1-32)
 * Używany zarówno przez obszary (1-11) jak i punkty (12-32)
 */
export interface MeasurementArea {
  nameId: string;
  value?: number;
  isFixed: boolean;
  isMeasured: boolean;
  status: PaintStatus;
  /** Czy pomiar nie ma zastosowania (element nie istnieje w pojeździe) */
  notApplicable?: boolean;
  /** Minimalna wartość pomiaru z API (μm) */
  minMeasurement?: number;
  /** Maksymalna wartość pomiaru z API (μm) */
  maxMeasurement?: number;
}

/**
 * Typ zmapowanych danych pomiarów lakieru i szczelin
 */
export interface MappedPaintAndGapMeasurements {
  measurementAreas: MeasurementArea[];
  gapPoints: GapPoint[];
  expertSummary?: string;
}

/**
 * Mapowanie wszystkich pomiarów lakieru (nameId 1-32) do zunifikowanej struktury
 */
export const mapAllPaintMeasurements = (
  measurements?: { [key: string]: PaintThicknessMeasurementDto },
): MeasurementArea[] => {
  if (!measurements) return [];

  return Object.entries(measurements)
    .filter(([nameId]) => {
      const num = parseInt(nameId, 10);
      return !isNaN(num) && num >= 1 && num <= 32;
    })
    .map(([nameId, measurement]) => {
      // Używamy minMeasurement dla określenia statusu (zakres kolorów)
      // bo maxMeasurement to górna granica zakresu i przy strict < wpada w następny bucket
      const displayValue = measurement.minMeasurement ?? measurement.maxMeasurement ?? undefined;

      const isMeasured = !measurement.notApplicable;
      const notApplicable = measurement.notApplicable || false;
      const status = notApplicable
        ? 'NOT_APPLICABLE'
        : measurementValueToPaintStatus(displayValue);

      return {
        nameId,
        value: displayValue,
        isFixed: measurement.isFixed || false,
        isMeasured,
        status,
        notApplicable,
        minMeasurement: measurement.minMeasurement,
        maxMeasurement: measurement.maxMeasurement,
      };
    });
};

/**
 * Główna funkcja mapowania sekcji pomiarów lakieru i szczelin
 */
export const mapPaintThicknessAndGapMeasurements = (
  subsection?: PaintThicknessAndGapMeasurementsSubsectionDto,
): MappedPaintAndGapMeasurements | null => {
  if (!subsection) return null;

  return {
    measurementAreas: mapAllPaintMeasurements(subsection.paintThicknessMeasurements),
    gapPoints: mapGapMeasurementsToPoints(subsection.gapsMeasurements),
    expertSummary: subsection.expertSummary,
  };
};
